import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';

type OnlineCallback = () => void;

/**
 * Network Status Service
 *
 * Detects connectivity changes on both Capacitor (native) and web.
 * Exposes a reactive `isOnline` property for templates and other services.
 *
 * - Capacitor: uses @capacitor/network for reliable native detection
 * - Web: uses navigator.onLine + window online/offline events
 */
export default class NetworkStatus extends Service {
  @tracked isOnline = globalThis.navigator?.onLine ?? true;

  private onlineCallbacks: OnlineCallback[] = [];
  private removeNativeListener: (() => Promise<void>) | null = null;

  constructor(owner: Owner) {
    super(owner);
    void this.setup();
  }

  /**
   * Register a callback invoked when connectivity returns (offline → online).
   * Returns an unsubscribe function.
   */
  onReconnect(callback: OnlineCallback): () => void {
    this.onlineCallbacks.push(callback);
    return () => {
      const idx = this.onlineCallbacks.indexOf(callback);
      if (idx !== -1) {
        this.onlineCallbacks.splice(idx, 1);
      }
    };
  }

  private async setup(): Promise<void> {
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        await this.setupNative();
        return;
      }
    } catch {
      // Not a Capacitor environment — fall through to web
    }
    this.setupWeb();
  }

  private async setupNative(): Promise<void> {
    const { Network } = await import('@capacitor/network');
    const status = await Network.getStatus();
    this.isOnline = status.connected;

    const handle = await Network.addListener(
      'networkStatusChange',
      (newStatus) => {
        const wasOffline = !this.isOnline;
        this.isOnline = newStatus.connected;
        if (wasOffline && newStatus.connected) {
          this.fireReconnect();
        }
      }
    );
    this.removeNativeListener = () => handle.remove();
  }

  private setupWeb(): void {
    this.isOnline = globalThis.navigator?.onLine ?? true;

    globalThis.addEventListener('online', this.handleOnline);
    globalThis.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = (): void => {
    this.isOnline = true;
    this.fireReconnect();
  };

  private handleOffline = (): void => {
    this.isOnline = false;
  };

  private fireReconnect(): void {
    for (const cb of this.onlineCallbacks) {
      try {
        cb();
      } catch (err) {
        console.error('[network-status] reconnect callback error:', err);
      }
    }
  }

  override willDestroy(): void {
    super.willDestroy();
    globalThis.removeEventListener('online', this.handleOnline);
    globalThis.removeEventListener('offline', this.handleOffline);
    void this.removeNativeListener?.();
  }
}

declare module '@ember/service' {
  interface Registry {
    'network-status': NetworkStatus;
  }
}
